import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import ReservationService from '~/services/ReservationService';

export type RatePlanStatus = 'content-approved' | 'draft';

export interface ChainRatePlanGroup {
  rate_plan_code: string | null;
  name: string;
  property_count: number;
  complete_count: number;
}

interface ChainRatePlansResponse {
  status: number;
  result: ChainRatePlanGroup[];
  count: number;
  total?: number;
}

export interface ChainRatePlansFilters {
  status: RatePlanStatus;
  page: number;
  limit: number;
  ratePlanCode?: string;
  propertySearch?: string;
}

export const chainRatePlanQueryKeys = {
  all: ['chains', 'ratePlans'] as const,
  list: (chainId: string, filters: ChainRatePlansFilters) => [...chainRatePlanQueryKeys.all, chainId, filters] as const,
};

export function useGetChainRatePlans(chainIds: string[], filters: ChainRatePlansFilters) {
  const queryResults = useQueries({
    queries: chainIds.map((id) => ({
      queryKey: chainRatePlanQueryKeys.list(id, filters),
      queryFn: ({ signal: _signal }) =>
        ReservationService.getRatePlansByChain(
          id,
          filters.status,
          filters.page,
          filters.limit,
          filters.ratePlanCode,
          filters.propertySearch,
          // TODO: pass signal to service once it supports AbortSignal
        ) as Promise<ChainRatePlansResponse>,
      enabled: chainIds.length > 0,
      placeholderData: (prev: ChainRatePlansResponse | undefined) => prev,
    })),
  });

  const isLoading = queryResults.some((q) => q.isLoading);
  const error = queryResults.find((q) => q.error)?.error ?? null;

  // TODO: Pagination is still approximate for multi-ID chains (e.g. ACCOR has 4 IDs).
  // Each query fires with the same page/limit, so totalCount is the sum of all chain totals.
  // A multi-chain-ID endpoint would fix this properly.
  const { ratePlanGroups, totalCount } = useMemo(() => {
    const groups = queryResults.flatMap((q) => q.data?.result ?? []);
    const total = queryResults.reduce((sum, q) => sum + (q.data?.total ?? q.data?.count ?? 0), 0);
    return { ratePlanGroups: groups, totalCount: total };
  }, [queryResults]);

  return { ratePlanGroups, totalCount, isLoading, error };
}
